import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

export function DeleteConfirm({
  trigger = null,
  children = null,
  defaultOpen = false,
  open,
  onOpenChange,
  onConfirm,
  title,
  buttonTitle,
  description,
}: {
  trigger?: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  buttonTitle?: string;
  description: string;
}) {
  const { t } = useTranslation();
  const [localOpen, setLocalOpen] = useState(defaultOpen ?? false);

  useEffect(() => {
    if (open !== undefined) {
      setLocalOpen(open);
    }
  }, [open]);

  function handleOnOpenChange(isOpen: boolean) {
    setLocalOpen(isOpen);
    onOpenChange?.(isOpen);
  }

  function localConfirm() {
    handleOnOpenChange(false);
    onConfirm();
  }

  return (
    <AlertDialog open={localOpen} onOpenChange={handleOnOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(title)}</AlertDialogTitle>
          <AlertDialogDescription>{t(description)}</AlertDialogDescription>
        </AlertDialogHeader>

        {children}

        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <Button variant="destructive" onClick={localConfirm}>
            {buttonTitle ? t(buttonTitle) : t("common.delete")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export type ChoiceDialogAction = {
  label: string;
  onClick: () => void;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost";
  disabled?: boolean;
};

/**
 * An alert dialog offering several ways forward, e.g. "Save and continue" / "Discard
 * changes" next to the cancel button. Labels should be verbs that say what happens,
 * not OK/Cancel. Unlike `DeleteConfirm`, texts are passed already translated.
 */
export function ChoiceDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelLabel,
  actions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  cancelLabel: string;
  actions: ChoiceDialogAction[];
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant ?? "default"}
              disabled={action.disabled}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
